import { useEffect, useMemo } from 'react';

import { Box } from 'components/Box';
import { UserNotification } from 'components/UserNotification';
import { useUserEnvironment } from 'modules/main/store/hooks/useUserEnvironment';
import { MappedNotification } from 'types/mappedNotifications/MappedNotification';
import { promoteAts } from 'utils/ats';
import {
  NOTIFICATIONS,
  NotificationStatus,
  useLocalNotifications,
  useUpdateLocalNotification,
} from 'utils/localNotifications';
import { getMappedNotificationIds } from 'utils/userNotifications';

import { LocalNotificationContent } from './LocalNotificationContent';

type Props = {
  notifications: MappedNotification[];
  dismisseable?: boolean;
  onClick?: () => void;
};

export function Notifications({
  notifications,
  dismisseable = true,
  onClick,
}: Props) {
  const userEnvironment = useUserEnvironment();

  const { localNotifications, isReady, isSet } = useLocalNotifications();

  const updateLocalNotification = useUpdateLocalNotification();

  // Only these notifications are shown in the dropdown:
  // - ATS CTA
  const localNotificationsFiltered = useMemo(
    () =>
      localNotifications?.filter(
        (notification) =>
          notification.status === NotificationStatus.NEW &&
          notification.type === NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA &&
          promoteAts({ userEnvironment }),
      ),
    [localNotifications, userEnvironment],
  );

  // Set initial status for the notifications
  // These local notifications support the read/unread behavior
  useEffect(() => {
    if (isReady && !isSet(NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA)) {
      updateLocalNotification(NOTIFICATIONS.ORG_ADMIN_USER_DROPDOWN_ATS_CTA, {
        status: NotificationStatus.NEW,
        read: false,
      });
    }
  }, [isReady, localNotifications, isSet, updateLocalNotification]);

  if (!isReady) {
    return null;
  }

  return (
    <>
      {localNotificationsFiltered?.length !== 0
        ? localNotificationsFiltered?.map((notification) => (
            <LocalNotificationContent
              key={notification.type}
              notification={notification}
              dismisseable={dismisseable}
              onClick={onClick}
            />
          ))
        : null}

      {notifications.length > 0
        ? notifications
            .sort((a, b) => Date.parse(b.created) - Date.parse(a.created))
            .map((notification) => (
              <UserNotification
                key={getMappedNotificationIds(notification).join()}
                notification={notification}
                dismisseable={dismisseable}
                onClick={onClick}
              />
            ))
        : null}

      {notifications.length === 0 &&
      localNotificationsFiltered?.length === 0 ? (
        <Box p="20px" width="100%" textAlign="center">
          {getText('You are all up to date! 🎉')}
        </Box>
      ) : null}
    </>
  );
}
